/** Геометрия плотной намотки: длина вдоль спирали сохраняется. Все размеры в метрах. */
export const FABRIC_THICKNESS = 0.0015;
export const TUBE_RADIUS = 0.02;
/** При положении 100% нижние 2% полотна остаются свободными. */
export const MAX_WOUND_FRACTION = 0.98;
const PITCH = FABRIC_THICKNESS / (2 * Math.PI);
const START_RADIUS = TUBE_RADIUS + FABRIC_THICKNESS / 2;

function arcLength(angle: number): number {
  const primitive = (r: number) =>
    (r * Math.hypot(r, PITCH) + PITCH * PITCH * Math.asinh(r / PITCH)) / (2 * PITCH);
  return primitive(START_RADIUS + PITCH * angle) - primitive(START_RADIUS);
}

export function windingAngle(length: number): number {
  let low = 0;
  let high = Math.max(0, length) / START_RADIUS;
  for (let i = 0; i < 35; i++) {
    const mid = (low + high) / 2;
    if (arcLength(mid) < length) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

export const BASE_WRAP = arcLength(2 * Math.PI);

export interface FabricPoint {
  y: number;
  z: number;
  rotation: number;
}

export function rollState(height: number, progress: number) {
  const wound = height * MAX_WOUND_FRACTION * Math.min(1, Math.max(0, progress));
  const angle = windingAngle(BASE_WRAP + wound);
  return { wound, angle, radius: START_RADIUS + PITCH * angle };
}

/** s — расстояние от верхней кромки ткани; отрицательное s — постоянный оборот на валу. */
export function fabricPoint(s: number, height: number, progress: number): FabricPoint {
  return fabricPointAt(s, rollState(height, progress));
}

export function fabricPointAt(
  s: number,
  state: ReturnType<typeof rollState>,
  theta = windingAngle(Math.max(0, BASE_WRAP + s)),
): FabricPoint {
  if (s >= state.wound) {
    return { y: -(s - state.wound), z: -state.radius, rotation: 0 };
  }
  const a = theta - state.angle;
  const radius = START_RADIUS + PITCH * theta;
  return {
    y: -radius * Math.sin(a),
    z: -radius * Math.cos(a),
    rotation: Math.atan2(
      PITCH * Math.cos(a) - radius * Math.sin(a),
      PITCH * Math.sin(a) + radius * Math.cos(a),
    ),
  };
}

/** Замкнутая петля цепи в YZ: две прямые и две полуокружности. */
export function chainPoint(distance: number, drop: number, radius: number) {
  const arc = Math.PI * radius;
  const perimeter = 2 * drop + 2 * arc;
  let s = ((distance % perimeter) + perimeter) % perimeter;
  if (s < drop) return { y: -s, z: radius };
  s -= drop;
  if (s < arc)
    return { y: -drop - radius * Math.sin(s / radius), z: radius * Math.cos(s / radius) };
  s -= arc;
  if (s < drop) return { y: -drop + s, z: -radius };
  s -= drop;
  return { y: radius * Math.sin(s / radius), z: -radius * Math.cos(s / radius) };
}
